import { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { signOut, onAuthStateChanged, sendEmailVerification } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, doc, getDoc, deleteDoc, writeBatch, updateDoc } from 'firebase/firestore';
import AddEvent from '../components/AddEvent';
import CustodySetup from '../components/CustodySetup';
import FamilySetup from '../components/FamilySetup';
import CustodyCalendar from '../components/CustodyCalendar';
import ParentLinking from '../components/ParentLinking';
import EditEvent from '../components/EditEvent';
import NotificationCenter from '../components/NotificationCenter';
import CalendarExport from '../components/CalendarExport';
import BottomTabBar from '../components/BottomTabBar';
import ExpenseTracker from '../components/ExpenseTracker';
import MessageThread from '../components/MessageThread';
import { createNotification } from '../services/NotificationService';
import RoutineSetup from '../components/RoutineSetup';
import RoutineCards from '../components/RoutineCards';
import FamilyMoodSummary from '../components/FamilyMoodSummary';
import ChildInvite from '../components/ChildInvite';
import { scheduleAllNotifications } from '../services/LocalNotificationService';
import { registerFCMToken } from '../services/FCMService';
import ChildMessages from '../components/ChildMessages';
import HandoffNotes from '../components/HandoffNotes';
import FamilyPhotos from '../components/FamilyPhotos';
import EventDetail from '../components/EventDetail';
import EmergencyContacts from '../components/EmergencyContacts';

const localDateStr = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

function ParentDashboard() {
  const navigate = useNavigate();
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showCustodySetup, setShowCustodySetup] = useState(false);
  const [showFamilySetup, setShowFamilySetup] = useState(false);
  const [showParentLinking, setShowParentLinking] = useState(false);
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [custodySchedule, setCustodySchedule] = useState(null);
  const [familyId, setFamilyId] = useState(null);
  const [linkedParentId, setLinkedParentId] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');
  const [activeTab, setActiveTab] = useState('events');

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDateRange] = useState({ start: '', end: '' });
  const [sortBy] = useState('date');

  const [showRoutineSetup, setShowRoutineSetup] = useState(false);
  const [showChildInvite, setShowChildInvite] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [familySubTab, setFamilySubTab] = useState('handoff');
  const [messagesSubTab, setMessagesSubTab] = useState('coparent');

  // Delete scope dialog state for recurring events
  const [showDeleteScopeDialog, setShowDeleteScopeDialog] = useState(false);
  const [deleteEventData, setDeleteEventData] = useState(null);
  const [deleteScope, setDeleteScope] = useState('THIS_ONLY');

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        registerFCMToken();
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  const loadEvents = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get user's familyId
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userFamilyId = userDoc.exists() && userDoc.data().familyId;
      
      const todayStr = localDateStr();
      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        where('familyId', '==', userFamilyId || user.uid),
        where('date', '>=', todayStr),
        orderBy('date', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      const eventsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setEvents(eventsData);
    } catch (err) {
      console.error('Error loading events:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustodySchedule = async () => {
    if (!user) return;
    
    try {
      const custodyDoc = await getDoc(doc(db, 'custody', user.uid));
      if (custodyDoc.exists()) {
        setCustodySchedule(custodyDoc.data());
      }
    } catch (err) {
      console.error('Error loading custody schedule:', err);
    }
  };

  const loadFamily = async () => {
    if (!user) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.familyId) setFamilyId(userData.familyId);
        if (userData.linkedParentId) setLinkedParentId(userData.linkedParentId);
        if (userData.name) setCurrentUserName(userData.name);

        // If not yet linked, check if a sent invite was accepted
        if (!userData.linkedParentId) {
          const inviteQuery = query(
            collection(db, 'parentInvites'),
            where('invitedBy', '==', user.uid),
            where('status', '==', 'accepted')
          );
          const inviteSnap = await getDocs(inviteQuery);
          if (!inviteSnap.empty) {
            const invite = inviteSnap.docs[0].data();
            await updateDoc(doc(db, 'users', user.uid), {
              linkedParentId: invite.acceptedBy,
              familyId: invite.familyId
            });
            setLinkedParentId(invite.acceptedBy);
            setFamilyId(invite.familyId);
          }
        }
      }
    } catch (err) {
      console.error('Error loading family:', err);
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      // Load the event to check if it's recurring
      const eventDoc = await getDoc(doc(db, 'events', eventId));
      if (!eventDoc.exists()) return;

      const eventData = eventDoc.data();

      // If recurring, show scope dialog; otherwise delete immediately
      if (eventData.isRecurring) {
        setDeleteEventData({ id: eventId, ...eventData });
        setDeleteScope('THIS_ONLY');
        setShowDeleteScopeDialog(true);
      } else {
        if (!window.confirm('Are you sure you want to delete this event?')) {
          return;
        }
        await performDelete(eventId, 'THIS_ONLY', null, null, eventData);
      }
    } catch (err) {
      console.error('Error preparing event deletion:', err);
      alert('Failed to delete event. Please try again.');
    }
  };

  const performDelete = async (eventId, scope, recurringGroupId, instanceIndex, eventDataOverride) => {
    try {
      if (scope === 'THIS_ONLY') {
        // Delete just this event
        await deleteDoc(doc(db, 'events', eventId));
      } else if (scope === 'THIS_AND_FOLLOWING') {
        // Delete this event and all following instances
        const batch = writeBatch(db);

        // Get all events in the recurring series
        const allInstancesQuery = query(
          collection(db, 'events'),
          where('recurringEventGroupId', '==', recurringGroupId)
        );
        const allInstancesSnapshot = await getDocs(allInstancesQuery);

        // Delete this and following instances
        allInstancesSnapshot.forEach(doc => {
          const docInstanceIndex = doc.data().instanceIndex || 0;
          if (docInstanceIndex >= instanceIndex) {
            batch.delete(doc.ref);
          }
        });

        await batch.commit();
      } else if (scope === 'ALL') {
        // Delete all instances in the series
        const batch = writeBatch(db);

        // Get all events in the recurring series
        const allInstancesQuery = query(
          collection(db, 'events'),
          where('recurringEventGroupId', '==', recurringGroupId)
        );
        const allInstancesSnapshot = await getDocs(allInstancesQuery);

        // Delete all instances
        allInstancesSnapshot.forEach(doc => {
          batch.delete(doc.ref);
        });

        await batch.commit();
      }

      // Notify co-parent about the deletion
      const deletedEventTitle = (eventDataOverride || deleteEventData)?.title;
      const deletedEventDate = (eventDataOverride || deleteEventData)?.date;
      if (deletedEventTitle && user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userName = (userDoc.exists() && userDoc.data().name) || 'Your co-parent';
        if (linkedParentId) {
          await createNotification(
            linkedParentId,
            'Event Removed',
            `${userName} removed "${deletedEventTitle}"`,
            'event_deleted',
            { eventTitle: deletedEventTitle, deletedBy: user.uid }
          );
        }
        // Notify child accounts in the family
        if (familyId) {
          const familyDoc = await getDoc(doc(db, 'families', familyId));
          if (familyDoc.exists()) {
            const members = familyDoc.data().members || [];
            for (const memberId of members) {
              if (memberId === user.uid || memberId === linkedParentId) continue;
              const memberDoc = await getDoc(doc(db, 'users', memberId));
              if (memberDoc.exists() && memberDoc.data().role === 'child') {
                const dateFormatted = deletedEventDate
                  ? new Date(deletedEventDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
                  : '';
                await createNotification(
                  memberId,
                  '📅 Something changed!',
                  `${deletedEventTitle}${dateFormatted ? ' on ' + dateFormatted : ''} has been cancelled. Your schedule has been updated.`,
                  'event_deleted_child',
                  { eventTitle: deletedEventTitle, eventDate: deletedEventDate }
                );
              }
            }
          }
        }
      }

      alert('Event deleted successfully!');
      loadEvents();
      setShowDeleteScopeDialog(false);
      setDeleteEventData(null);
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event. Please try again.');
    }
  };

  const openEditEvent = (eventId) => {
    setEditingEventId(eventId);
    setShowEditEvent(true);
  };

  // Load events, custody, and family when user is ready
  useEffect(() => {
    if (user) {
      loadEvents();
      loadCustodySchedule();
      loadFamily();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Schedule local notifications whenever events or custody schedule change
  useEffect(() => {
    scheduleAllNotifications({ events, custodySchedule });
  }, [events, custodySchedule]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Get filtered and sorted events
  const getFilteredAndSortedEvents = () => {
    const today = localDateStr();

    // For recurring events, only keep the next upcoming instance per group
    const recurringGroupMap = {};
    const nonRecurring = [];

    events.forEach(event => {
      if (event.isRecurring && event.recurringEventGroupId) {
        const gid = event.recurringEventGroupId;
        if (event.date >= today) {
          if (!recurringGroupMap[gid] || event.date < recurringGroupMap[gid].date) {
            recurringGroupMap[gid] = event;
          }
        }
      } else {
        nonRecurring.push(event);
      }
    });

    let filtered = [...nonRecurring.filter(e => e.date >= today), ...Object.values(recurringGroupMap)];

    // Search filter (title + location)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(event =>
        (event.title && event.title.toLowerCase().includes(term)) ||
        (event.location && event.location.toLowerCase().includes(term)) ||
        (event.category && event.category.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (filterCategory) {
      filtered = filtered.filter(event => event.category === filterCategory);
    }

    // Date range filter
    if (filterDateRange.start) {
      filtered = filtered.filter(event => event.date >= filterDateRange.start);
    }
    if (filterDateRange.end) {
      filtered = filtered.filter(event => event.date <= filterDateRange.end);
    }

    // Sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'category':
          return a.category.localeCompare(b.category);
        case 'date':
        default:
          return new Date(a.date) - new Date(b.date);
      }
    });

    return sorted;
  };

  const filteredEvents = getFilteredAndSortedEvents();

  // Show loading while checking auth
  if (!user) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontFamily: 'system-ui'
      }}>
        <p style={{ color: '#666' }}>Loading...</p>
      </div>
    );
  }

  const tabs = [
    { id: 'events', icon: '🏠', label: 'Events' },
    { id: 'calendar', icon: '🗓️', label: 'Calendar' },
    { id: 'messages', icon: '💬', label: 'Messages' },
    { id: 'family', icon: '👨‍👩‍👧', label: 'Family' },
    { id: 'expenses', icon: '💰', label: 'Expenses' },
    { id: 'settings', icon: '⚙️', label: 'More' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #f0f4ff 0%, #fdf0f8 100%)', fontFamily: 'system-ui', paddingBottom: '72px' }}>

      {/* Email verification banner */}
      {user && !user.emailVerified && (
        <div style={{
          background: '#fff8e1', borderBottom: '1px solid #ffe082',
          padding: '10px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '10px', flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', color: '#7c5c00' }}>
            ✉️ Please verify your email address to secure your account.
          </span>
          <button
            onClick={async () => { await sendEmailVerification(user); alert('Verification email sent!'); }}
            style={{
              background: '#ffa000', color: 'white', border: 'none',
              borderRadius: '6px', padding: '4px 12px', fontSize: '12px',
              fontWeight: 'bold', cursor: 'pointer'
            }}
          >Resend Email</button>
        </div>
      )}

      {/* Sticky header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #9b59b6 100%)',
        padding: '16px 20px 14px',
        boxShadow: '0 4px 20px rgba(102,126,234,0.3)',
        position: 'sticky', top: 0, zIndex: 50
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, color: 'white', fontSize: '20px', fontWeight: '800', letterSpacing: '-0.3px' }}>Kinnect</h1>
            <p style={{ margin: '2px 0 0 0', color: 'rgba(255,255,255,0.75)', fontSize: '12px', fontWeight: '500' }}>
              {currentUserName ? `Welcome back, ${currentUserName}` : 'Parent Dashboard'}
            </p>
          </div>
          <NotificationCenter userId={user.uid} />
        </div>
      </div>

      <div style={{ padding: '16px', maxWidth: '700px', margin: '0 auto' }}>

        {/* EVENTS TAB */}
        {activeTab === 'events' && (
          <div>
            <FamilyMoodSummary familyId={familyId} />
            <button
              onClick={() => setShowAddEvent(true)}
              style={{
                width: '100%',
                padding: '15px',
                background: 'linear-gradient(135deg, #667eea 0%, #9b59b6 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(102,126,234,0.4)',
                letterSpacing: '0.2px'
              }}
            >
              <span style={{ fontSize: '22px', lineHeight: 1 }}>+</span> Add New Event
            </button>

            {/* Search */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search events…"
                style={{
                  flex: 1, padding: '11px 16px', border: '1.5px solid #e8e8f0',
                  borderRadius: '12px', fontSize: '15px', boxSizing: 'border-box',
                  background: 'white', outline: 'none', color: '#333',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} style={{
                  padding: '10px 14px', background: 'white', border: '1.5px solid #e8e8f0',
                  borderRadius: '12px', cursor: 'pointer', color: '#666', fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>✕</button>
              )}
            </div>

            {/* Category pills */}
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '4px' }}>
              {['All', 'School', 'Sports', 'Medical', 'Activities', 'Family', 'Other'].map(cat => {
                const active = (cat === 'All' && filterCategory === '') || filterCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setFilterCategory(cat === 'All' ? '' : (filterCategory === cat ? '' : cat))}
                    style={{
                      padding: '7px 15px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                      background: active ? 'linear-gradient(135deg, #667eea, #9b59b6)' : 'white',
                      color: active ? 'white' : '#666',
                      fontWeight: '600', fontSize: '13px', flexShrink: 0, whiteSpace: 'nowrap',
                      boxShadow: active ? '0 3px 10px rgba(102,126,234,0.35)' : '0 1px 4px rgba(0,0,0,0.08)'
                    }}
                  >{cat}</button>
                );
              })}
            </div>

            {/* Events List */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#aaa', background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                <div style={{ fontSize: '14px' }}>Loading events…</div>
              </div>
            ) : events.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#aaa', background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📅</div>
                <div style={{ fontWeight: '700', color: '#555', fontSize: '16px', marginBottom: '6px' }}>No events yet</div>
                <div style={{ fontSize: '14px' }}>Tap "Add New Event" to get started</div>
              </div>
            ) : filteredEvents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#aaa', background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                <div style={{ fontWeight: '700', color: '#555', fontSize: '16px' }}>No matches</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filteredEvents.map(event => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      padding: '14px',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                      cursor: 'pointer',
                      border: `1px solid ${event.color}30`,
                    }}
                  >
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      {/* Colored icon circle */}
                      <div style={{
                        width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0,
                        background: `${event.color}18`, border: `2px solid ${event.color}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '24px'
                      }}>{event.icon}</div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                          <div style={{ fontWeight: '700', color: '#222', fontSize: '15px', lineHeight: 1.3 }}>{event.title}</div>
                          <div style={{
                            background: event.color, color: 'white', padding: '2px 8px',
                            borderRadius: '20px', fontSize: '10px', fontWeight: '700', flexShrink: 0, whiteSpace: 'nowrap'
                          }}>{event.category}</div>
                        </div>
                        <div style={{ color: '#888', fontSize: '12px', marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          <span>📅 {formatDate(event.date)}</span>
                          {event.time && <span>🕐 {event.time}</span>}
                          {event.location && <span>📍 {event.location}</span>}
                        </div>
                        {event.notes && <div style={{ color: '#aaa', fontSize: '11px', marginTop: '3px' }}>{event.notes}</div>}

                        {/* Badges + action row */}
                        <div style={{ display: 'flex', gap: '6px', marginTop: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                          {event.isRecurring && (
                            <span style={{
                              background: '#667eea15', color: '#667eea', padding: '3px 8px',
                              borderRadius: '20px', fontSize: '11px', fontWeight: '700', border: '1px solid #667eea30'
                            }}>🔄 Recurring</span>
                          )}
                          {event.rsvp && user && event.rsvp[user.uid] && (
                            <span style={{
                              padding: '3px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                              background: event.rsvp[user.uid] === 'yes' ? '#34a85320' : event.rsvp[user.uid] === 'maybe' ? '#ffa50020' : '#ff444420',
                              color: event.rsvp[user.uid] === 'yes' ? '#34a853' : event.rsvp[user.uid] === 'maybe' ? '#e09000' : '#ff4444',
                              border: `1px solid ${event.rsvp[user.uid] === 'yes' ? '#34a85350' : event.rsvp[user.uid] === 'maybe' ? '#ffa50050' : '#ff444450'}`
                            }}>
                              {event.rsvp[user.uid] === 'yes' ? '✅ Going' : event.rsvp[user.uid] === 'maybe' ? '🤔 Maybe' : '❌ Not going'}
                            </span>
                          )}
                          <div style={{ flex: 1 }} />
                          <button onClick={e => { e.stopPropagation(); openEditEvent(event.id); }} style={{
                            background: '#667eea', color: 'white', border: 'none',
                            borderRadius: '8px', padding: '5px 14px', cursor: 'pointer',
                            fontWeight: '700', fontSize: '12px'
                          }}>Edit</button>
                          <button onClick={e => { e.stopPropagation(); deleteEvent(event.id); }} style={{
                            background: 'white', color: '#ff4444', border: '1.5px solid #ff444460',
                            borderRadius: '8px', padding: '5px 14px', cursor: 'pointer',
                            fontWeight: '700', fontSize: '12px'
                          }}>Delete</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CALENDAR TAB */}
        {activeTab === 'calendar' && (
          <div>
            {custodySchedule ? (
              <CustodyCalendar custodySchedule={custodySchedule} events={events} />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <p style={{ fontSize: '40px', margin: '0 0 12px 0' }}>🏠</p>
                <p style={{ color: '#666', marginBottom: '16px' }}>No custody schedule set up yet.</p>
                <button onClick={() => setShowCustodySetup(true)} style={{
                  padding: '12px 24px', background: '#4facfe', color: 'white',
                  border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer'
                }}>Set Up Custody Schedule</button>
              </div>
            )}
            {custodySchedule && (
              <button onClick={() => setShowCustodySetup(true)} style={{
                width: '100%', marginTop: '12px', padding: '12px',
                background: 'white', color: '#4facfe', border: '2px solid #4facfe',
                borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px'
              }}>Edit Custody Schedule</button>
            )}
            <CalendarExport events={events} custodySchedule={custodySchedule} />
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div>
            {/* Sub-tab toggle */}
            <div style={{
              display: 'flex', background: 'white', borderRadius: '14px',
              padding: '4px', marginBottom: '16px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
            }}>
              {[
                { id: 'coparent', icon: '👥', label: 'Co-Parent' },
                { id: 'family', icon: '👨‍👩‍👧', label: 'Family Chat' },
              ].map(st => (
                <button
                  key={st.id}
                  onClick={() => setMessagesSubTab(st.id)}
                  title={st.label}
                  style={{
                    flex: 1, padding: '10px 14px', borderRadius: '10px', border: 'none',
                    cursor: 'pointer',
                    background: messagesSubTab === st.id ? 'linear-gradient(135deg, #667eea, #9b59b6)' : 'transparent',
                    boxShadow: messagesSubTab === st.id ? '0 3px 10px rgba(102,126,234,0.35)' : 'none',
                    transition: 'all 0.2s',
                    fontSize: messagesSubTab === st.id ? '26px' : '22px',
                  }}
                >{st.icon}</button>
              ))}
            </div>

            {messagesSubTab === 'coparent' && (
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                <MessageThread
                  familyId={familyId}
                  linkedParentId={linkedParentId}
                  currentUserName={currentUserName}
                  fullPage
                />
              </div>
            )}

            {messagesSubTab === 'family' && familyId && (
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                <ChildMessages
                  familyId={familyId}
                  userId={user?.uid}
                  userName={currentUserName}
                />
              </div>
            )}
            {messagesSubTab === 'family' && !familyId && (
              <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', color: '#aaa' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>👨‍👩‍👧</div>
                <div style={{ fontWeight: '600', color: '#666' }}>Set up your family first to chat with children</div>
              </div>
            )}
          </div>
        )}

        {/* FAMILY TAB */}
        {activeTab === 'family' && (
          <div>
            {/* Sub-tab toggle */}
            <div style={{
              display: 'flex', background: 'white', borderRadius: '14px',
              padding: '4px', marginBottom: '16px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              overflowX: 'auto', gap: '2px'
            }}>
              {[
                { id: 'handoff', icon: '🤝', label: 'Handoff' },
                { id: 'routines', icon: '🏠', label: 'Routines' },
                { id: 'contacts', icon: '📋', label: 'Contacts' },
                { id: 'photos', icon: '📸', label: 'Photos' },
              ].map(st => (
                <button
                  key={st.id}
                  onClick={() => setFamilySubTab(st.id)}
                  title={st.label}
                  style={{
                    flex: 1, padding: '10px 8px', borderRadius: '10px', border: 'none',
                    cursor: 'pointer',
                    background: familySubTab === st.id ? 'linear-gradient(135deg, #667eea, #9b59b6)' : 'transparent',
                    boxShadow: familySubTab === st.id ? '0 3px 10px rgba(102,126,234,0.35)' : 'none',
                    transition: 'all 0.2s',
                    fontSize: familySubTab === st.id ? '26px' : '22px',
                  }}
                >{st.icon}</button>
              ))}
            </div>

            {/* Handoff sub-tab */}
            {familySubTab === 'handoff' && familyId && (
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                <HandoffNotes familyId={familyId} currentUserName={currentUserName} />
              </div>
            )}
            {familySubTab === 'handoff' && !familyId && (
              <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', color: '#aaa' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>🤝</div>
                <div style={{ fontWeight: '600', color: '#666' }}>Set up your family first to use handoff notes</div>
              </div>
            )}

            {/* Routines sub-tab */}

            {familySubTab === 'routines' && familyId && (
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <h3 style={{ margin: 0, color: '#333', fontSize: '15px', fontWeight: '700' }}>🏠 Daily Routines</h3>
                  <button onClick={() => setShowRoutineSetup(true)} style={{
                    padding: '7px 16px', background: 'linear-gradient(135deg, #667eea, #9b59b6)', color: 'white',
                    border: 'none', borderRadius: '10px', fontWeight: '700',
                    cursor: 'pointer', fontSize: '13px', boxShadow: '0 3px 10px rgba(102,126,234,0.3)'
                  }}>+ Add</button>
                </div>
                <RoutineCards familyId={familyId} editable={true} />
              </div>
            )}
            {familySubTab === 'routines' && !familyId && (
              <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', color: '#aaa' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>🏠</div>
                <div style={{ fontWeight: '600', color: '#666' }}>Set up your family first to add routines</div>
              </div>
            )}

            {/* Contacts sub-tab */}
            {familySubTab === 'contacts' && (
              familyId ? (
                <div style={{ background: 'white', borderRadius: '16px', padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                  <EmergencyContacts familyId={familyId} editable={true} />
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', color: '#aaa' }}>
                  <div style={{ fontSize: '40px', marginBottom: '10px' }}>📋</div>
                  <div style={{ fontWeight: '600', color: '#666' }}>Set up your family first to add contacts</div>
                </div>
              )
            )}

            {/* Photos sub-tab */}
            {familySubTab === 'photos' && (
              <FamilyPhotos familyId={familyId} />
            )}
          </div>
        )}

        {/* EXPENSES TAB */}
        {activeTab === 'expenses' && (
          familyId ? (
            <ExpenseTracker
              familyId={familyId}
              linkedParentId={linkedParentId}
              currentUserName={currentUserName}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <p style={{ fontSize: '40px', margin: '0 0 12px 0' }}>💰</p>
              <p style={{ color: '#666' }}>Set up a family group to track shared expenses.</p>
            </div>
          )
        )}

        {/* MORE TAB */}
        {activeTab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {/* Family Setup */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>Family Setup</h3>
              <button onClick={() => setShowFamilySetup(true)} style={{
                width: '100%', padding: '12px', marginBottom: '8px',
                background: familyId ? 'white' : '#34a853', color: familyId ? '#34a853' : 'white',
                border: familyId ? '2px solid #34a853' : 'none',
                borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px'
              }}>👨‍👩‍👧 {familyId ? `Family Code: ${familyId}` : 'Set Up Family'}</button>
              <button onClick={() => setShowParentLinking(true)} style={{
                width: '100%', padding: '12px', marginBottom: '8px',
                background: linkedParentId ? 'white' : '#ff6b9d',
                color: linkedParentId ? '#ff6b9d' : 'white',
                border: linkedParentId ? '2px solid #ff6b9d' : 'none',
                borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px'
              }}>👥 {linkedParentId ? 'Co-Parent Linked ✓' : 'Link Co-Parent'}</button>
              {familyId && (
                <button onClick={() => setShowChildInvite(true)} style={{
                  width: '100%', padding: '12px',
                  background: 'white', color: '#43a047',
                  border: '2px solid #43a047',
                  borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px'
                }}>👧 Invite Child</button>
              )}
            </div>

            {/* Account */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>Account</h3>
              <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '14px' }}>Logged in as <strong>{user.email}</strong></p>
              <p style={{ margin: '0 0 12px 0', color: '#888', fontSize: '13px' }}>Name: {currentUserName}</p>
              <a href="/privacy-policy" style={{
                display: 'block', padding: '12px', color: '#667eea', marginBottom: '8px',
                textDecoration: 'none', fontSize: '14px', fontWeight: 'bold',
                border: '2px solid #667eea', borderRadius: '10px', textAlign: 'center'
              }}>🔒 Privacy Policy</a>
              <button onClick={handleLogout} style={{
                width: '100%', padding: '12px', background: 'white', color: '#ff4444',
                border: '2px solid #ff4444', borderRadius: '10px', fontWeight: 'bold',
                cursor: 'pointer', fontSize: '14px'
              }}>Log Out</button>
            </div>
          </div>
        )}

      </div>{/* end inner content padding div */}

      <BottomTabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Add Event Modal */}
      {showAddEvent && (
        <AddEvent
          onClose={() => setShowAddEvent(false)}
          onEventAdded={loadEvents}
          linkedParentId={linkedParentId}
        />
      )}

      {/* Custody Setup Modal */}
      {showCustodySetup && (
        <CustodySetup 
          onClose={() => setShowCustodySetup(false)}
          onScheduleSet={loadCustodySchedule}
        />
      )}

      {/* Family Setup Modal */}
      {showFamilySetup && (
        <FamilySetup 
          onClose={() => setShowFamilySetup(false)}
          onFamilyJoined={() => {
            loadFamily();
            loadEvents();
          }}
        />
      )}

      {/* Parent Linking Modal */}
      {showParentLinking && (
        <ParentLinking
          onClose={() => setShowParentLinking(false)}
          onParentLinked={() => {
            loadFamily();
            loadEvents();
          }}
          familyId={familyId}
        />
      )}

      {/* Edit Event Modal */}
      {showEditEvent && editingEventId && (
        <EditEvent
          eventId={editingEventId}
          onClose={() => {
            setShowEditEvent(false);
            setEditingEventId(null);
          }}
          onEventUpdated={loadEvents}
          linkedParentId={linkedParentId}
        />
      )}

      {/* Event Detail Sheet */}
      {selectedEvent && (
        <EventDetail
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onEdit={(id) => { setSelectedEvent(null); openEditEvent(id); }}
          onDelete={(id) => { setSelectedEvent(null); deleteEvent(id); }}
        />
      )}

      {/* Child Invite Modal */}
      {showChildInvite && familyId && (
        <ChildInvite familyId={familyId} onClose={() => setShowChildInvite(false)} />
      )}

      {/* Routine Setup Modal */}
      {showRoutineSetup && familyId && (
        <RoutineSetup
          familyId={familyId}
          onClose={() => setShowRoutineSetup(false)}
        />
      )}

      {/* Delete Scope Dialog for Recurring Events */}
      {showDeleteScopeDialog && deleteEventData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ margin: '0 0 8px 0', color: '#333' }}>Delete Recurring Event</h2>
            <p style={{ color: '#666', marginBottom: '24px', lineHeight: 1.5 }}>
              Which events do you want to delete?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                border: deleteScope === 'THIS_ONLY' ? '2px solid #ff4444' : '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                background: deleteScope === 'THIS_ONLY' ? '#ff444415' : 'white',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  name="deleteScope"
                  value="THIS_ONLY"
                  checked={deleteScope === 'THIS_ONLY'}
                  onChange={(e) => setDeleteScope(e.target.value)}
                  style={{ marginRight: '12px', cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '2px' }}>Only this event</div>
                  <div style={{ fontSize: '13px', color: '#888' }}>Delete just this single occurrence</div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                border: deleteScope === 'THIS_AND_FOLLOWING' ? '2px solid #ff4444' : '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                background: deleteScope === 'THIS_AND_FOLLOWING' ? '#ff444415' : 'white',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  name="deleteScope"
                  value="THIS_AND_FOLLOWING"
                  checked={deleteScope === 'THIS_AND_FOLLOWING'}
                  onChange={(e) => setDeleteScope(e.target.value)}
                  style={{ marginRight: '12px', cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '2px' }}>This and following events</div>
                  <div style={{ fontSize: '13px', color: '#888' }}>Delete this event and all future occurrences</div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                border: deleteScope === 'ALL' ? '2px solid #ff4444' : '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                background: deleteScope === 'ALL' ? '#ff444415' : 'white',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  name="deleteScope"
                  value="ALL"
                  checked={deleteScope === 'ALL'}
                  onChange={(e) => setDeleteScope(e.target.value)}
                  style={{ marginRight: '12px', cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '2px' }}>All events in series</div>
                  <div style={{ fontSize: '13px', color: '#888' }}>Delete every occurrence of this recurring event</div>
                </div>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => performDelete(
                  deleteEventData.id,
                  deleteScope,
                  deleteEventData.recurringEventGroupId,
                  deleteEventData.instanceIndex || 0
                )}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteScopeDialog(false);
                  setDeleteEventData(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'white',
                  color: '#333',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParentDashboard;